import type { AgenticLogicBindingFieldsProps } from "./AgenticLogicBindingFieldsProps";
import { IgAlert } from "@intelligems/ig-theme/IgAlert";
import { IgSelect } from "@intelligems/ig-theme/IgSelect";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import type { AgenticLogicPlacementDto } from "@intelligems/ig-types/dtos/agentic-logic/placements";
import { stripMigrationProvenanceMarker } from "@intelligems/ig-types/dtos/agentic-logic/migration-provenance";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import type { AgenticLogicSystemRulePhase } from "@src/api/agentic-logic/ensure-agentic-logic-system-rule";
import { useAgenticLogicAssignments } from "@src/api/agentic-logic/list-agentic-logic-assignments";
import { useAgenticLogicPlacements } from "@src/api/agentic-logic/list-agentic-logic-placements";
import { useAgenticLogicRules } from "@src/api/agentic-logic/list-agentic-logic-rules";
import { useGetOrg } from "@src/api/organizations/get-organization";
import { useDismissAlert } from "@src/hooks/useDismissAlert";
import { useSelector } from "@src/store";
import { getIsAlertDismissed } from "@src/store/slices/appSettings";
import { buildAgenticLogicRuleOptions } from "./agenticLogicRuleOptions";
import { otherExperienceCount } from "./otherExperienceCount";
import { ruleUsages } from "@src/features/ai/pages/agentic-logic/rules/ruleListRows";
import ButtonBase from "@mui/material/ButtonBase";
import pluralize from "pluralize";
import { useTheme } from "@mui/material/styles";
import { TriangleAlert } from "lucide-react";
import CircularProgress from "@mui/material/CircularProgress";

const NONE = "";
const RULES_MOVED_ALERT_ID = "agentic-logic-rules-moved";
/**
 * The "+ New rule" row's value. It is an option in the same list rather than a
 * button beside it because that is where an author looks when the rule they
 * want is not there — but it selects nothing, so it is filtered out before the
 * change reaches `onRuleChange`.
 */
const CREATE_RULE_OPTION = "__create__";
/**
 * The built-in row's value while the built-in rule has no id yet (it is still
 * being provisioned). Like the "+ New rule" row it selects nothing, so it never
 * reaches `onRuleChange`; the host binds the real id once it exists.
 */
const BUILT_IN_PENDING_OPTION = "__built_in_pending__";

/**
 * A built-in rule a surface offers FIRST in its menu, ahead of the store's own
 * rules — today the Shopify recommendations rule on Checkout > Upsell blocks.
 */
export interface AgenticLogicBuiltInRuleOption {
  label: string;
  /** Null until the store's copy of the rule exists. */
  ruleId: string | null;
  description: string | null;
  phase: AgenticLogicSystemRulePhase;
  /**
   * True when the container has no rule yet and the host is about to bind it
   * to this one as its default. The row then shows as selected — with its
   * provisioning state — instead of leaving the field blank.
   */
  isPendingDefault: boolean;
}

/**
 * The write side of a surface's Agentic Logic binding, shared by every
 * container that has one — a checkout block, a post-purchase block, an onsite
 * upsell component.
 *
 * Only the RULE is chosen here. The placement and the (placement, variation)
 * assignment are derived by the backend when the experience is saved, so
 * picking a rule writes nothing on its own and abandoning the editor cannot
 * leave an orphaned placement behind. Everything the assignment itself carries
 * — its budget, the experience it belongs to — is shown read-only and edited
 * on the Assignments tab, which owns it.
 *
 * A rule selection is mandatory: there is no "no rule" option, so a container
 * either serves a rule or is not bound at all.
 *
 * There is no empty-result picker any more either. What a slot does when its
 * rule returns nothing is one of the RULE's settings, beside how many products
 * it returns and which ones it refuses — so it is chosen once, on the rule,
 * rather than restated on every container the rule fills.
 */
export const AgenticLogicBindingFields = ({
  placementId,
  ruleId,
  variationId,
  surface,
  onRuleChange,
  onCreateRule,
  onEditRule,
  builtInRule,
}: AgenticLogicBindingFieldsProps) => {
  const theme = useTheme();
  const org = useGetOrg();
  const dismissAlert = useDismissAlert();
  const isRulesMovedAlertDismissed = useSelector(
    getIsAlertDismissed(org.data?.org?.id, RULES_MOVED_ALERT_ID)
  );
  // `isLoading`, not `isFetching`: the rule list revalidates on every mount and
  // window focus, so reading `isFetching` here would disable a populated
  // dropdown — and blink its empty-state helper off — on every open and every
  // tab-back. The control stays usable while the list revalidates and is
  // disabled only while there is nothing to show.
  const { data, isLoading, isError } = useAgenticLogicRules();
  const placements = useAgenticLogicPlacements();
  const assignments = useAgenticLogicAssignments();

  const builtInValue = builtInRule?.ruleId ?? BUILT_IN_PENDING_OPTION;
  const selectedIsBuiltIn =
    !!builtInRule &&
    (builtInRule.isPendingDefault ||
      (builtInRule.ruleId !== null && ruleId === builtInRule.ruleId));
  const selected = selectedIsBuiltIn ? builtInValue : (ruleId ?? NONE);
  const options = buildAgenticLogicRuleOptions(
    data?.rules ?? [],
    surface,
    ruleId
  );
  const selectableRules = options.selectable;
  const selectedIsUnavailable =
    options.selectedIsUnavailable && !selectedIsBuiltIn;
  // The rule's own plain-language description, shown beside the picker so an
  // author can confirm what the selection does without opening the builder.
  const selectedRuleSummary = selectedIsBuiltIn
    ? builtInRule.description || undefined
    : ruleId
      ? stripMigrationProvenanceMarker(
          data?.rules.find((rule) => rule.id === ruleId)?.description
        ) || undefined
      : undefined;

  const placement: AgenticLogicPlacementDto | undefined = placementId
    ? placements.data?.placements.find((p) => p.id === placementId)
    : undefined;

  // The cell this container actually serves: its own variation's assignment,
  // falling back to the org-wide one, which is what serving resolves too.
  const assignment = placementId
    ? (assignments.data?.assignments.find(
        (a) => a.placementId === placementId && a.variationId === variationId
      ) ??
      assignments.data?.assignments.find(
        (a) => a.placementId === placementId && a.variationId === null
      ))
    : undefined;

  const showEdit =
    !!onEditRule && selected !== NONE && !selectedIsBuiltIn && !isError;

  // The experience this container belongs to, read off its own variation's
  // assignment — the one usage that is not "other".
  const currentExperienceId =
    assignments.data?.assignments.find((a) => a.variationId === variationId)
      ?.experience?.id ?? null;
  const otherExperiences =
    ruleId && !selectedIsBuiltIn && data
      ? otherExperienceCount(
          ruleUsages({
            ruleId,
            // A failed read is unknown, not "used nowhere".
            placements: placements.isError
              ? undefined
              : placements.data?.placements,
            assignments: assignments.isError
              ? undefined
              : assignments.data?.assignments,
          }),
          currentExperienceId
        )
      : null;

  return (
    <Grid container spacing={3}>
      <Grid size={12}>
        <IgAlert
          alertKey={RULES_MOVED_ALERT_ID}
          severity="info"
          message="Rules have moved — they now live under Store management, so you can reuse one across experiences."
          isDismissed={isRulesMovedAlertDismissed}
          onDismiss={() => dismissAlert(RULES_MOVED_ALERT_ID)}
        />
      </Grid>
      <Grid size={12}>
        <IgSelect
          label="Assigned rule"
          labelProps={{ variant: "body1", color: "text.primary" }}
          fullWidth
          value={isError ? NONE : selected}
          disabled={isLoading || isError}
          onChange={(e) => {
            if (e.target.value === CREATE_RULE_OPTION) {
              onCreateRule?.();
              return;
            }
            if (e.target.value === BUILT_IN_PENDING_OPTION) {
              return;
            }
            onRuleChange(e.target.value);
          }}
          // Figma "field": Edit sits inside the trigger, 8px left of the
          // chevron, and opens the rule rather than the menu. The chevron is
          // 16px at a 7px inset; the input's own 14px right padding already
          // covers part of that, so 17px more lands the 8px gap.
          endAdornment={
            showEdit ? (
              <ButtonBase
                disableRipple
                onMouseDown={(event) => event.stopPropagation()}
                onClick={() => onEditRule?.(selected)}
                sx={{ flexShrink: 0, mr: "17px" }}
              >
                <IgTypography
                  variant="body1"
                  fontWeight={600}
                  sx={{ color: theme.palette.text.interactive.accent.default }}
                >
                  Edit
                </IgTypography>
              </ButtonBase>
            ) : undefined
          }
        >
          {builtInRule ? (
            <MenuItem value={builtInValue}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <span>{builtInRule.label}</span>
                {builtInRule.phase === "provisioning" ? (
                  <CircularProgress
                    size={12}
                    aria-label={`Setting up ${builtInRule.label}`}
                  />
                ) : null}
              </Stack>
            </MenuItem>
          ) : null}
          {selectedIsUnavailable && (
            <MenuItem value={selected}>
              Currently attached rule (unavailable)
            </MenuItem>
          )}
          {selectableRules.map((rule) => (
            <MenuItem value={rule.id} key={rule.id}>
              {rule.name}
            </MenuItem>
          ))}
          {onCreateRule ? (
            <MenuItem
              value={CREATE_RULE_OPTION}
              sx={{
                color: theme.palette.text.interactive.accent.default,
                fontWeight: 600,
              }}
            >
              + New rule
            </MenuItem>
          ) : null}
        </IgSelect>
        {otherExperiences !== null && otherExperiences > 0 && (
          <Stack direction="row" alignItems="center" gap={2} sx={{ mt: 2 }}>
            <TriangleAlert
              aria-hidden
              size={13}
              color={theme.igIconColors.warning}
            />
            <IgTypography
              variant="body2"
              fontWeight={600}
              sx={{ color: theme.igIconColors.warning }}
            >
              Used in {otherExperiences} other{" "}
              {pluralize("experience", otherExperiences)}
            </IgTypography>
          </Stack>
        )}
        {selectedIsBuiltIn && builtInRule.phase === "provisioning" && (
          <IgTypography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Setting up {builtInRule.label} for this store. The block starts
            showing products once it&apos;s live.
          </IgTypography>
        )}
        {selectedIsBuiltIn && builtInRule.phase === "failed" && (
          <IgTypography variant="body2" color="error" sx={{ mt: 1 }}>
            {builtInRule.label} couldn&apos;t be set up for this store. Choose
            another rule, or try again later.
          </IgTypography>
        )}
        {!builtInRule &&
          !isLoading &&
          !isError &&
          selectableRules.length === 0 &&
          !selectedIsUnavailable && (
            <IgTypography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {" "}
              No rules are written for this placement yet.
            </IgTypography>
          )}
      </Grid>
      {selectedRuleSummary && !isError && (
        <Grid container size={12} direction={"column"} spacing={1}>
          <IgTypography variant="body1">Rule summary</IgTypography>
          <IgTypography variant="body2" color="text.secondary">
            {selectedRuleSummary}
          </IgTypography>
        </Grid>
      )}
      {isError && (
        <Grid size={12}>
          <IgTypography variant="body2" color="error">
            Could not load rules. The placement keeps its current rule until
            this loads.
          </IgTypography>
        </Grid>
      )}
    </Grid>
  );
};
